// parlayService.js
// Service for parlay-related API calls: parlay events, generate parlays

import { POST, BASE_URL } from './httpClient';

const normalizeSportKey = (sport) => String(sport || '').trim().toUpperCase();
const formatOptionLabel = (value) => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeOptionItem = (item, fallbackKey = 'value') => {
    if (typeof item === 'string') {
        return {
            key: item,
            value: item,
            label: formatOptionLabel(item)
        };
    }

    if (!item || typeof item !== 'object') {
        return null;
    }

    const key = item.type ||
        item.market_key ||
        item.key ||
        item.value ||
        item.id ||
        item[fallbackKey];

    if (!key) {
        return null;
    }

    return {
        key,
        value: key,
        label: item.displayName || item.display_name || item.label || item.name || formatOptionLabel(key),
        count: item.count ?? null
    };
};

const normalizeOptionsPayload = (payload = {}, source = 'scoutingReports') => {
    const normalizedSource = source === 'statAnalysis' ? 'statAnalysis' : 'scoutingReports';

    if (normalizedSource === 'statAnalysis') {
        const propTypesBySportRaw = payload?.propTypesBySport || {};
        const propTypesBySport = Object.entries(propTypesBySportRaw).reduce((acc, [sport, propTypes]) => {
            acc[normalizeSportKey(sport)] = (Array.isArray(propTypes) ? propTypes : [])
                .map((item) => normalizeOptionItem(item, 'market_key'))
                .filter(Boolean);
            return acc;
        }, {});

        return {
            source: normalizedSource,
            snipedTypes: (Array.isArray(payload?.snipedTypes) ? payload.snipedTypes : [])
                .map((item) => normalizeOptionItem(item))
                .filter(Boolean),
            snipedBetTypes: (Array.isArray(payload?.snipedBetTypes) ? payload.snipedBetTypes : [])
                .map((item) => normalizeOptionItem(item))
                .filter(Boolean),
            propTypes: (Array.isArray(payload?.propTypes) ? payload.propTypes : [])
                .map((item) => normalizeOptionItem(item, 'market_key'))
                .filter(Boolean),
            propTypesBySport
        };
    }

    return {
        source: normalizedSource,
        betTypes: (Array.isArray(payload?.betTypes) ? payload.betTypes : [])
            .map((item) => normalizeOptionItem(item, 'type'))
            .filter(Boolean),
        propMarketTypes: (Array.isArray(payload?.propMarketTypes) ? payload.propMarketTypes : [])
            .map((item) => normalizeOptionItem(item, 'type'))
            .filter(Boolean),
        propDirections: (Array.isArray(payload?.propDirections) ? payload.propDirections : [])
            .map((item) => normalizeOptionItem(item, 'type'))
            .filter(Boolean)
    };
};

/**
 * Parlay generation functions
 */

// Function to get parlay events for today
export const getParlayEvents = async (sport = null) => {
    try {
        const requestBody = sport ? { sport } : {};
        const response = await POST(`${BASE_URL}/parlay/events`, requestBody);
        return response;
    } catch (error) {
        console.error('Error fetching parlay events:', error);
        throw error;
    }
};

export const getParlayOptions = async ({ sports = [], source = 'scoutingReports' } = {}) => {
    try {
        const normalizedSports = (Array.isArray(sports) ? sports : [])
            .map(normalizeSportKey)
            .filter(Boolean);

        const requestBody = {
            source
        };

        if (normalizedSports.length === 1) {
            requestBody.sport = normalizedSports[0];
        } else if (normalizedSports.length > 1) {
            requestBody.sports = normalizedSports;
        }

        const response = await POST(`${BASE_URL}/parlay/options`, requestBody);
        const payload = response?.data?.results || {};

        return {
            response,
            options: normalizeOptionsPayload(payload, source)
        };
    } catch (error) {
        console.error('Error fetching parlay options:', error);
        throw error;
    }
};

/**
 * Generate parlays with configuration
 * Supports two data sources: scouting reports (default) and stat analysis (sniped odds)
 * 
 * @param {string[]} sports - Optional array of sports to filter (empty array = all sports)
 * @param {number} numEvents - Number of bets per parlay (2-20)
 * @param {number} maxParlays - Maximum parlays to generate (1-100)
 * @param {string[]} betTypes - Optional array of bet types to include (empty array = all types) - only for scouting reports
 * @param {string[]} propMarketTypes - Optional array of prop market types (over/under) when Prop is selected - only for scouting reports
 * @param {string} source - Data source: "scoutingReports" (default) or "statAnalysis"
 * @param {string} snipedType - Optional when source is "statAnalysis": "overs" or "unders"
 * @param {object} snipedFilters - Optional filters for stat analysis source
 * @returns {Promise} API response
 */
export const generateParlays = async (
    sports = [], 
    numEvents = 3, 
    maxParlays = 10, 
    betTypes = [], 
    propMarketTypes = [],
    propDirections = [],
    source = 'scoutingReports',
    snipedType = null,
    snipedFilters = {}
) => {
    try {
        // Build base request body with camelCase field names (API accepts both)
        const requestBody = {
            numEvents: numEvents,
            maxParlays: maxParlays
        };
        
        // Handle source-specific parameters
        if (source === 'statAnalysis') {
            // Stat analysis (sniped odds) source
            requestBody.source = 'statAnalysis';
            
            // Sniped type is optional for stat analysis.
            if (snipedType) {
                requestBody.snipedType = snipedType;
            }
            
            // Sports filter - use multi-select array at top level (per user decision)
            if (sports.length > 0) {
                requestBody.sports = sports;
            }
            
            // Build snipedFilters object if any filters are provided
            const filters = {};
            
            // Add sniped filters if provided
            if (snipedFilters.betType) {
                filters.betType = snipedFilters.betType;
            }
            if (snipedFilters.propTypes && snipedFilters.propTypes.length > 0) {
                filters.propTypes = snipedFilters.propTypes;
            }
            if (snipedFilters.isDfs !== null && snipedFilters.isDfs !== undefined) {
                filters.isDfs = snipedFilters.isDfs;
            }
            if (snipedFilters.recommendation) {
                filters.recommendation = snipedFilters.recommendation;
            }
            if (snipedFilters.minValueScore !== null && snipedFilters.minValueScore !== undefined) {
                filters.minValueScore = snipedFilters.minValueScore;
            }
            if (snipedFilters.limit) {
                filters.limit = snipedFilters.limit;
            }
            if (snipedFilters.includeAllOpportunities !== null && snipedFilters.includeAllOpportunities !== undefined) {
                filters.includeAllOpportunities = snipedFilters.includeAllOpportunities;
            }
            
            // Only include snipedFilters if it has any properties
            if (Object.keys(filters).length > 0) {
                requestBody.snipedFilters = filters;
            }
        } else {
            // Scouting reports source (default)
            // Only include sports if any are selected (empty array means all sports)
            if (sports.length > 0) {
                requestBody.sports = sports;
            }
            
            // Only include betTypes if any are selected (empty array means all types)
            if (betTypes.length > 0) {
                requestBody.betTypes = betTypes;
            }
            
            // Only include prop filters if Prop is selected and any are chosen
            if (betTypes.includes('Prop') && propMarketTypes.length > 0) {
                requestBody.propMarketTypes = propMarketTypes;
            }
            if (betTypes.includes('Prop') && propDirections.length > 0) {
                requestBody.propDirections = propDirections;
            }
        }
        
        const response = await POST(`${BASE_URL}/parlay/generate`, requestBody);
        return response;
    } catch (error) {
        console.error('Error generating parlays:', error);
        throw error;
    }
};
